/**
 * In-slide chart → two-pass translator for Office.js mode.
 *
 * Office.js cannot insert a native PowerPoint chart inline into an existing
 * slide. The LLM emits charts inline (`shape.action="create"` with a `chart`
 * field on a slide modification) per the FactSet COM Chart wire format, but
 * in Office.js mode we transparently rewrite that into:
 *
 *   1. Generate a single-slide PPTX containing the chart via PptxGenJS.
 *   2. Insert that slide after the source slide using
 *      `presentation.insertSlidesFromBase64`.
 *   3. Rewrite the source slide modification: drop the chart shape, and if
 *      the source slide had OTHER shape modifications, retarget those onto
 *      the newly inserted chart slide (so callouts, text overlays, etc. land
 *      on the chart slide and not on the source).
 *
 * This is invoked from `applyModifications` BEFORE the usual slide-iteration
 * pass, but ONLY in Office.js mode. FactSet COM bridge mode skips this and
 * forwards `shape.chart` untouched to `PerformBatchPptOperations`.
 */

import {
  PresentationModification,
  ShapeModification,
  SlideModification,
} from "../types";
import {
  generateChartSlideBase64,
  insertChartSlideFromChartInfo,
} from "./nativeChartService";
import { guardedPowerPointRun, isOfficeJsBypassed } from "./officeJsGuard";

export type TranslatorLogger = (message: string) => void;

function defaultLogger(message: string): void {
  console.log(message);
}

function hasChartShape(shape: ShapeModification): boolean {
  return shape.action === "create" && !!shape.chart;
}

function slideHasChart(slide: SlideModification): boolean {
  if (!slide.shapes || slide.shapes.length === 0) {
    return false;
  }
  return slide.shapes.some(hasChartShape);
}

function isBypassMode(): boolean {
  return isOfficeJsBypassed();
}

async function insertChartSlideBase64(
  base64: string,
  insertAfterSlideId: string | undefined,
  log: TranslatorLogger
): Promise<{ insertedSlideId?: string; insertedSlideIndex?: number }> {
  let insertedSlideId: string | undefined;
  let insertedSlideIndex: number | undefined;

  await guardedPowerPointRun(async (context) => {
    const slidesBefore = context.presentation.slides;
    slidesBefore.load("items/id");
    await context.sync();
    const beforeIds = new Set(slidesBefore.items.map((slide) => slide.id));

    const insertOptions: PowerPoint.InsertSlideOptions = {
      formatting: PowerPoint.InsertSlideFormatting.useDestinationTheme,
    };
    if (insertAfterSlideId && slidesBefore.items.some((slide) => slide.id === insertAfterSlideId)) {
      insertOptions.targetSlideId = insertAfterSlideId;
      log(`[chartTwoPassTranslator] inserting after slide ${insertAfterSlideId}`);
    } else {
      log(`[chartTwoPassTranslator] no anchor slide found; inserting at end`);
    }

    context.presentation.insertSlidesFromBase64(base64, insertOptions);
    await context.sync();

    const slidesAfter = context.presentation.slides;
    slidesAfter.load("items/id");
    await context.sync();

    const insertedSlide = slidesAfter.items.find((slide) => !beforeIds.has(slide.id));
    if (insertedSlide) {
      insertedSlideId = insertedSlide.id;
      insertedSlideIndex = slidesAfter.items.findIndex((slide) => slide.id === insertedSlide.id);
    }
  });

  return { insertedSlideId, insertedSlideIndex };
}

/**
 * Expand any in-slide chart shapes (`shape.action="create"` with a `chart`
 * field) into newly inserted chart slides via Office.js. Non-chart shape
 * modifications on those slides are retargeted onto the newly inserted chart
 * slide so overlays land on top of the chart.
 *
 * Multiple chart shapes on the same source slide each produce their own
 * inserted slide; non-chart overlays follow the FIRST inserted chart slide
 * (matches the legacy two-pass behavior).
 *
 * In bypass mode (NOP), this is a no-op: the chart shape is dropped from the
 * modifications since there is no live presentation to insert into.
 */
export async function expandInSlideChartsForOfficeJs(
  modifications: PresentationModification,
  log: TranslatorLogger = defaultLogger
): Promise<PresentationModification> {
  if (!modifications.slides || modifications.slides.length === 0) {
    return modifications;
  }

  const hasAnyChart = modifications.slides.some(slideHasChart);
  if (!hasAnyChart) {
    return modifications;
  }

  if (isBypassMode()) {
    log(`[chartTwoPassTranslator] OfficeJS bypassed; dropping chart shapes from modifications`);
    const stripped = modifications.slides.map((slide) => ({
      ...slide,
      shapes: slide.shapes ? slide.shapes.filter((shape) => !hasChartShape(shape)) : slide.shapes,
    }));
    return { ...modifications, slides: stripped };
  }

  const rewrittenSlides: SlideModification[] = [];

  for (const slide of modifications.slides) {
    if (!slideHasChart(slide)) {
      rewrittenSlides.push(slide);
      continue;
    }

    const chartShapes = slide.shapes.filter(hasChartShape);
    const overlayShapes = slide.shapes.filter((shape) => !hasChartShape(shape));

    let firstInsertedSlideId: string | undefined;
    let firstInsertedSlideIndex: number | undefined;
    let anchorSlideId: string | undefined = slide.id;

    for (let i = 0; i < chartShapes.length; i++) {
      const chartShape = chartShapes[i];
      if (!chartShape.chart) {
        continue;
      }
      try {
        log(
          `[chartTwoPassTranslator] generating chart slide for shape ${chartShape.id} ` +
            `(chartType=${chartShape.chart.chartType})`
        );
        const base64 = await generateChartSlideBase64(chartShape.chart);
        const { insertedSlideId, insertedSlideIndex } = await insertChartSlideBase64(
          base64,
          anchorSlideId,
          log
        );
        if (!firstInsertedSlideId && insertedSlideId) {
          firstInsertedSlideId = insertedSlideId;
          firstInsertedSlideIndex = insertedSlideIndex;
        }
        if (insertedSlideId) {
          // Chain subsequent chart slides AFTER the just-inserted one so they
          // land in emission order.
          anchorSlideId = insertedSlideId;
        }
        log(
          `[chartTwoPassTranslator] inserted chart slide ${insertedSlideId ?? "?"} ` +
            `(index=${insertedSlideIndex ?? "?"})`
        );
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        log(`[chartTwoPassTranslator] FAILED to insert chart slide: ${msg}`);
        // Fall through: the chart shape is dropped from the rewrite, but the
        // remaining shape mods still apply to the source slide.
      }
    }

    if (overlayShapes.length === 0) {
      // No overlays to retarget; emit the source slide minus the chart
      // shapes, preserving slide id/index so any pre-existing non-chart
      // edits (rare given we filtered) still apply.
      rewrittenSlides.push({
        ...slide,
        shapes: overlayShapes,
      });
      continue;
    }

    if (firstInsertedSlideId !== undefined) {
      // Retarget overlays onto the FIRST inserted chart slide. Other
      // chart slides (if any) get no overlays.
      rewrittenSlides.push({
        ...slide,
        id: firstInsertedSlideId,
        index: firstInsertedSlideIndex ?? slide.index,
        shapes: overlayShapes,
        action: "modify",
      });
    } else {
      // No chart was inserted (all failed); keep overlays on the source.
      rewrittenSlides.push({
        ...slide,
        shapes: overlayShapes,
      });
    }
  }

  return { ...modifications, slides: rewrittenSlides };
}

// Re-export for callers that still want the direct insertion helper.
export { insertChartSlideFromChartInfo };
